/**
 * Relevancy Genie Component
 * PDF page filtering tool - allows clients to select relevant pages
 * Uses pdf-lib for manipulation and generates thumbnail previews
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

interface PageSelection {
  pageNumber: number;
  selected: boolean;
  thumbnail?: string;
}

interface RelevancyGenieProps {
  file: File;
  onComplete: (filteredBlob: Blob, selectedPages: number[], originalPageCount: number) => void;
  onCancel: () => void;
  onSkip: () => void;  // Use full document
  pageThreshold?: number;  // Show Genie if > this many pages
}

const RelevancyGenie: React.FC<RelevancyGenieProps> = ({
  file,
  onComplete,
  onCancel,
  onSkip,
  pageThreshold = 5
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pages, setPages] = useState<PageSelection[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showGenie, setShowGenie] = useState(false);
  const [fileUrl, setFileUrl] = useState<string>('');
  const [selectMode, setSelectMode] = useState<'include' | 'exclude'>('include');

  // Create file URL on mount
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setFileUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);

    // Initialize pages array
    const pageArray: PageSelection[] = [];
    for (let i = 1; i <= numPages; i++) {
      pageArray.push({
        pageNumber: i,
        selected: true  // All selected by default
      });
    }
    setPages(pageArray);

    // Show Genie if over threshold
    if (numPages > pageThreshold) {
      setShowGenie(true);
    } else {
      // Under threshold - skip Genie
      onSkip();
    }

    setLoading(false);
  }, [pageThreshold, onSkip]);

  const togglePage = (pageNumber: number) => {
    setPages(prev => prev.map(p =>
      p.pageNumber === pageNumber
        ? { ...p, selected: !p.selected }
        : p
    ));
  };

  const selectAll = () => {
    setPages(prev => prev.map(p => ({ ...p, selected: true })));
  };

  const deselectAll = () => {
    setPages(prev => prev.map(p => ({ ...p, selected: false })));
  };

  const invertSelection = () => {
    setPages(prev => prev.map(p => ({ ...p, selected: !p.selected })));
  };

  const selectedCount = pages.filter(p => p.selected).length;

  const processAndFilter = async () => {
    setProcessing(true);

    try {
      // Import pdf-lib dynamically
      const { PDFDocument } = await import('pdf-lib');

      // Read the original file
      const arrayBuffer = await file.arrayBuffer();
      const originalPdf = await PDFDocument.load(arrayBuffer);

      // Create new PDF with selected pages
      const filteredPdf = await PDFDocument.create();

      const selectedPageNumbers = pages
        .filter(p => p.selected)
        .map(p => p.pageNumber - 1);  // pdf-lib uses 0-indexed pages

      if (selectedPageNumbers.length === 0) {
        alert('Please select at least one page');
        setProcessing(false);
        return;
      }

      // Copy selected pages
      const copiedPages = await filteredPdf.copyPages(originalPdf, selectedPageNumbers);
      copiedPages.forEach(page => filteredPdf.addPage(page));

      // Add metadata indicating client filtering
      filteredPdf.setTitle(file.name);
      filteredPdf.setCreator('ShopDrawings.AI Relevancy Genie');
      filteredPdf.setProducer('Client-Filtered');
      filteredPdf.setSubject(`Original: ${numPages} pages, Filtered: ${selectedPageNumbers.length} pages`);

      // Generate blob - convert Uint8Array to ArrayBuffer for Blob compatibility
      const pdfBytes = await filteredPdf.save();
      const outputBuffer = pdfBytes.slice().buffer as ArrayBuffer;
      const blob = new Blob([outputBuffer], { type: 'application/pdf' });

      // Return filtered result
      onComplete(
        blob,
        pages.filter(p => p.selected).map(p => p.pageNumber),
        numPages
      );

    } catch (error) {
      console.error('PDF processing error:', error);
      alert('Failed to process PDF. Please try again.');
    }

    setProcessing(false);
  };

  if (loading) {
    return (
      <div className="genie-loading">
        <div className="loader"></div>
        <p>Analyzing document...</p>
      </div>
    );
  }

  if (!showGenie) {
    return null;
  }

  return (
    <div className="relevancy-genie">
      <div className="genie-header">
        <div className="genie-icon">🧞</div>
        <div className="genie-title">
          <h2>Relevancy Genie</h2>
          <p>This document has {numPages} pages. Select only the relevant pages to reduce costs.</p>
        </div>
      </div>

      <div className="genie-stats">
        <div className="stat">
          <span className="stat-value">{numPages}</span>
          <span className="stat-label">Total Pages</span>
        </div>
        <div className="stat">
          <span className="stat-value">{selectedCount}</span>
          <span className="stat-label">Selected</span>
        </div>
        <div className="stat">
          <span className="stat-value">${selectedCount.toFixed(2)}</span>
          <span className="stat-label">Page Fee</span>
        </div>
        <div className="stat savings">
          <span className="stat-value">${(numPages - selectedCount).toFixed(2)}</span>
          <span className="stat-label">Savings</span>
        </div>
      </div>

      <div className="genie-toolbar">
        <button onClick={selectAll}>Select All</button>
        <button onClick={deselectAll}>Deselect All</button>
        <button onClick={invertSelection}>Invert</button>
        <span className="selection-hint">
          Click pages to toggle selection
        </span>
      </div>

      <div className="page-grid">
        {pages.map((page) => (
          <div
            key={page.pageNumber}
            className={`page-thumbnail ${page.selected ? 'selected' : 'deselected'}`}
            onClick={() => togglePage(page.pageNumber)}
          >
            <div className="thumbnail-wrapper">
              <Document file={fileUrl} loading="">
                <Page
                  pageNumber={page.pageNumber}
                  width={150}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>
              <div className="page-overlay">
                <div className={`selection-indicator ${page.selected ? 'active' : ''}`}>
                  {page.selected ? '✓' : ''}
                </div>
              </div>
            </div>
            <div className="page-label">Page {page.pageNumber}</div>
          </div>
        ))}
      </div>

      <div className="genie-actions">
        <button className="btn-skip" onClick={onSkip}>
          Use Full Document (${numPages.toFixed(2)})
        </button>
        <button className="btn-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button
          className="btn-confirm"
          onClick={processAndFilter}
          disabled={selectedCount === 0 || processing}
        >
          {processing ? 'Processing...' : `Use Selected Pages (${selectedCount})`}
        </button>
      </div>

      <style>{`
        .relevancy-genie {
          background: var(--bg-primary);
          border-radius: 12px;
          padding: 24px;
          max-width: 1000px;
          margin: 0 auto;
        }

        .genie-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 48px;
        }

        .genie-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 24px;
        }

        .genie-icon {
          font-size: 48px;
        }

        .genie-title h2 {
          margin: 0 0 4px 0;
          font-size: 24px;
        }

        .genie-title p {
          margin: 0;
          color: var(--text-secondary);
        }

        .genie-stats {
          display: flex;
          gap: 24px;
          margin-bottom: 24px;
          padding: 16px;
          background: var(--bg-secondary);
          border-radius: 8px;
        }

        .stat {
          text-align: center;
        }

        .stat-value {
          display: block;
          font-size: 24px;
          font-weight: 700;
        }

        .stat-label {
          font-size: 12px;
          color: var(--text-secondary);
          text-transform: uppercase;
        }

        .stat.savings .stat-value {
          color: #10b981;
        }

        .genie-toolbar {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid var(--border-color, #334155);
        }

        .genie-toolbar button {
          padding: 8px 16px;
          border: 1px solid var(--border-color, #334155);
          border-radius: 6px;
          background: var(--bg-secondary);
          color: var(--text-primary);
          cursor: pointer;
          font-size: 13px;
        }

        .genie-toolbar button:hover {
          background: var(--bg-tertiary, #334155);
        }

        .selection-hint {
          margin-left: auto;
          font-size: 13px;
          color: var(--text-secondary);
        }

        .page-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(170px, 1fr));
          gap: 16px;
          max-height: 500px;
          overflow-y: auto;
          padding: 8px;
          margin-bottom: 24px;
        }

        .page-thumbnail {
          cursor: pointer;
          transition: all 0.2s;
        }

        .thumbnail-wrapper {
          position: relative;
          border: 3px solid transparent;
          border-radius: 8px;
          overflow: hidden;
          background: white;
        }

        .page-thumbnail.selected .thumbnail-wrapper {
          border-color: #10b981;
        }

        .page-thumbnail.deselected .thumbnail-wrapper {
          opacity: 0.4;
          border-color: #ef4444;
        }

        .page-thumbnail:hover .thumbnail-wrapper {
          transform: scale(1.02);
        }

        .page-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
        }

        .selection-indicator {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 18px;
          opacity: 0;
          transition: opacity 0.2s;
        }

        .page-thumbnail:hover .selection-indicator,
        .selection-indicator.active {
          opacity: 1;
        }

        .selection-indicator.active {
          background: #10b981;
        }

        .page-label {
          text-align: center;
          font-size: 12px;
          color: var(--text-secondary);
          margin-top: 4px;
        }

        .genie-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }

        .genie-actions button {
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          font-size: 14px;
        }

        .btn-skip {
          background: var(--bg-secondary);
          color: var(--text-primary);
          border: 1px solid var(--border-color, #334155) !important;
        }

        .btn-cancel {
          background: var(--bg-tertiary, #334155);
          color: var(--text-primary);
        }

        .btn-confirm {
          background: var(--primary);
          color: white;
        }

        .btn-confirm:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* react-pdf overrides */
        .react-pdf__Page {
          display: flex;
          justify-content: center;
        }

        .react-pdf__Page canvas {
          max-width: 100% !important;
          height: auto !important;
        }

        @media (max-width: 768px) {
          .genie-stats {
            flex-wrap: wrap;
          }

          .stat {
            flex: 1;
            min-width: 80px;
          }

          .page-grid {
            grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
          }

          .genie-actions {
            flex-direction: column;
          }

          .genie-actions button {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default RelevancyGenie;
